import { useSortable } from '@dnd-kit/sortable';
import { autoUpdate } from '@floating-ui/dom';
import { CopyIcon, DragHandleDots2Icon, TrashIcon } from '@radix-ui/react-icons';
import { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '../../../../primitives/button';
import { Separator } from '../../../../primitives/separator';
import { BlockFormSave } from './block-form-save';
import { BlockMenu } from './block-menu';

import { cn } from '../../../../utils/cn';

type BlockWrapperProps = {
  blockId: string;
  children: ReactNode;
  existingBlockNames?: string[];
  blockType:
    | 'header'
    | 'text'
    | 'button'
    | 'image'
    | 'avatar'
    | 'divider'
    | 'space'
    | 'html'
    | 'column'
    | 'container'
    | 'block';
  isSelected: boolean;
  isStructureLocked?: boolean;
  onDelete: () => void;
  onDuplicate?: () => void;
  onInsertBlock: (position: 'before' | 'after', blockType: BlockWrapperProps['blockType']) => void;
  onSaveBlock?: (name: string, description: string) => void | Promise<void>;
  parentListId: string;
  onSelect: () => void;
};

function BlockControlsOverlay({
  anchor,
  children,
}: {
  anchor: HTMLDivElement;
  children: ReactNode;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay) return;

    return autoUpdate(
      anchor,
      overlay,
      () => {
        const bounds = anchor.getBoundingClientRect();
        Object.assign(overlay.style, {
          left: `${bounds.left}px`,
          top: `${bounds.top}px`,
          width: `${bounds.width}px`,
          height: `${bounds.height}px`,
        });
      },
      { animationFrame: true },
    );
  }, [anchor]);

  return createPortal(
    <div
      ref={overlayRef}
      className="pointer-events-none fixed z-50 [&>*]:pointer-events-auto"
      data-block-controls
    >
      {children}
    </div>,
    document.body,
  );
}

function BlockTypeBadge({
  blockType,
  isSelected,
}: {
  blockType: BlockWrapperProps['blockType'];
  isSelected: boolean;
}) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute top-0 right-0 px-1 py-0 text-[10px] font-bold tracking-widest text-accent-foreground uppercase select-none',
        'transition-opacity duration-150',
        isSelected ? 'bg-accent/80 opacity-100' : 'bg-accent/90 opacity-0 group-hover:opacity-100',
      )}
    >
      {blockType}
    </span>
  );
}

function BlockDeleteButton({ onDelete }: { onDelete: () => void }) {
  return (
    <Button
      aria-label="Borrar elemento"
      className="size-7"
      data-block-delete-button
      size="icon-sm"
      title="Borrar elemento"
      variant="destructive"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDelete();
      }}
    >
      <TrashIcon />
    </Button>
  );
}

function BlockDuplicateButton({ onDuplicate }: { onDuplicate: () => void }) {
  return (
    <Button
      aria-label="Duplicar elemento"
      className="size-7"
      data-block-duplicate-button
      size="icon-sm"
      title="Duplicar elemento"
      variant="secondary"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDuplicate();
      }}
    >
      <CopyIcon />
    </Button>
  );
}

function BlockDragHandle({
  listeners,
  attributes,
  setActivatorNodeRef,
}: {
  listeners: ReturnType<typeof useSortable>['listeners'];
  attributes: ReturnType<typeof useSortable>['attributes'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
}) {
  return (
    <Button
      aria-label="Arrastrar elemento"
      data-block-dnd-handle
      className="size-7"
      ref={setActivatorNodeRef}
      size="icon-sm"
      title="Arrastrar elemento"
      variant="secondary"
      onMouseDown={(event) => {
        event.stopPropagation();
      }}
      {...attributes}
      {...listeners}
    >
      <DragHandleDots2Icon />
    </Button>
  );
}

export function BlockWrapper({
  blockId,
  children,
  existingBlockNames = [],
  blockType,
  isSelected,
  isStructureLocked = false,
  onDelete,
  onDuplicate,
  onInsertBlock,
  onSaveBlock,
  parentListId,
  onSelect,
}: BlockWrapperProps) {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: blockId,
    data: { parentListId },
    disabled: isStructureLocked,
  });
  const [isDirectlyHovered, setIsDirectlyHovered] = useState(false);
  const [anchor, setAnchor] = useState<HTMLDivElement | null>(null);
  const setWrapperRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      setAnchor(node);
    },
    [setNodeRef],
  );

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.65 : 1,
  };

  return (
    <div
      data-block-wrapper
      ref={setWrapperRef}
      style={style}
      role="button"
      aria-pressed={isSelected}
      tabIndex={0}
      onClickCapture={(event) => {
        const target = event.target as HTMLElement | null;

        if (
          target?.closest('[data-block-delete-button]') ||
          target?.closest('[data-block-duplicate-button]') ||
          target?.closest('[data-block-dnd-handle]') ||
          target?.closest('[data-block-menu-trigger]') ||
          target?.closest('[data-block-menu-content]') ||
          target?.closest('[data-block-sheet-content]')
        ) {
          return;
        }

        event.preventDefault();
        onSelect();
      }}
      onKeyDown={(event) => {
        const target = event.target as HTMLElement | null;

        // Radix portals (e.g. block-form-save sheet) still bubble React synthetic
        // events up through this handler even though they render outside this DOM subtree.
        if (target?.closest('[data-block-sheet-content], input, textarea, [contenteditable]')) {
          return;
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      onMouseMove={(event) => {
        const closestWrapper = (event.target as HTMLElement | null)?.closest(
          '[data-block-wrapper]',
        );
        setIsDirectlyHovered(closestWrapper === event.currentTarget);
      }}
      onMouseLeave={() => {
        setIsDirectlyHovered(false);
      }}
      className={cn('group relative w-full cursor-pointer', {
        'z-10': isDirectlyHovered && !isSelected,
        'z-20': isSelected,
      })}
    >
      {isDirectlyHovered && !isSelected ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 border-2 border-accent/80"
        />
      ) : null}

      {isSelected ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-20 border-2 border-accent/80"
        />
      ) : null}

      <div className="absolute top-0 right-0 z-10">
        <BlockTypeBadge blockType={blockType} isSelected={isSelected} />
      </div>

      {isSelected && anchor ? (
        <BlockControlsOverlay anchor={anchor}>
          {!isStructureLocked || (blockType === 'container' && onSaveBlock) ? (
            <div className="absolute -top-[2.8rem] left-0 z-30 flex items-center gap-1.5 rounded-full border bg-card p-1.5 shadow-md">
              {isStructureLocked ? null : (
                <BlockDragHandle
                  attributes={attributes}
                  listeners={listeners}
                  setActivatorNodeRef={setActivatorNodeRef}
                />
              )}
              {onDuplicate ? <BlockDuplicateButton onDuplicate={onDuplicate} /> : null}
              {blockType === 'container' && onSaveBlock ? (
                <BlockFormSave existingNames={existingBlockNames} onSave={onSaveBlock} />
              ) : null}
              {isStructureLocked ? null : (
                <>
                  <Separator orientation="vertical" className="h-7" />
                  <BlockDeleteButton onDelete={onDelete} />
                </>
              )}
            </div>
          ) : null}

          {isStructureLocked ? null : (
            <>
              <div className="absolute -top-3.5 left-1/2 z-30 -translate-x-1/2 rounded-full bg-card">
                <BlockMenu
                  position="top"
                  onSelectBlockType={(nextBlockType) => onInsertBlock('before', nextBlockType)}
                />
              </div>

              <div className="absolute -bottom-3.5 left-1/2 z-30 -translate-x-1/2 rounded-full bg-card">
                <BlockMenu
                  position="bottom"
                  onSelectBlockType={(nextBlockType) => onInsertBlock('after', nextBlockType)}
                />
              </div>
            </>
          )}
        </BlockControlsOverlay>
      ) : null}
      {children}
    </div>
  );
}
